const norm = (v) => (v == null ? "" : String(v).trim());

export const pickFirst = (...values) => {
  for (const value of values) {
    if (typeof value === "string") {
      const s = norm(value);
      if (s) return s;
      continue;
    }

    if (value != null) return value;
  }

  return "";
};

export const pickNumber = (...values) => {
  for (const value of values) {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }

  return 0;
};

export const pickBoolean = (...values) => {
  for (const value of values) {
    if (typeof value === "boolean") return value;
  }

  return null;
};

const asObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

export function normalizeOfferSnapshot(offer = null, overview = null) {
  const safeOffer = asObject(offer);
  const ov = asObject(overview);
  const totals = asObject(ov.totals || ov.summary || ov.metrics);
  const offerNode = asObject(ov.offer || ov.offerDetails);
  const promoNode = asObject(ov.promotion || ov.promo);
  const destinationNode = asObject(ov.destination || ov.destinationDetails);
  const topInvite = asObject(ov.topInvite || totals.topInvite);

  const spendValue = pickNumber(
    ov.spendValue,
    ov.spend,
    ov.cost,
    totals.spendValue,
    totals.spend,
    totals.cost,
    safeOffer.spendValue,
    safeOffer.spend,
    safeOffer.cost
  );

  const roiValueRaw = pickFirst(
    ov.roiValue,
    ov.roiMultiple,
    ov.roi,
    totals.roiValue,
    totals.roiMultiple,
    totals.roi,
    safeOffer.roiValue,
    safeOffer.roiMultiple,
    safeOffer.roi
  );

  const roiValueNum = Number(roiValueRaw);
  const roiValue = Number.isFinite(roiValueNum) ? roiValueNum : null;

  const conversionRateRaw = pickFirst(
    ov.conversionRate,
    ov.conversion_rate,
    totals.conversionRate,
    totals.conversion_rate,
    safeOffer.conversionRate,
    safeOffer.conversion_rate
  );

  const conversionRateNum = Number(conversionRateRaw);
  const conversionRate = Number.isFinite(conversionRateNum)
    ? conversionRateNum
    : null;

  return {
    title:
      pickFirst(
        offerNode.title,
        ov.title,
        safeOffer.title,
        offerNode.name,
        ov.name,
        safeOffer.name
      ) || "Offer",

    code: pickFirst(offerNode.code, ov.code, safeOffer.code) || "",

    active: pickBoolean(
      ov.active,
      offerNode.active,
      safeOffer.active,
      ov.isActive,
      offerNode.isActive,
      safeOffer.isActive
    ),

    updatedAt:
      pickFirst(
        ov.updatedAt,
        ov.updated_at,
        ov.lastUpdated,
        ov.last_updated,
        offerNode.updatedAt,
        offerNode.updated_at,
        safeOffer.updatedAt,
        safeOffer.updated_at,
        safeOffer.createdAt,
        safeOffer.created_at
      ) || "",

    inviteCount: pickNumber(
      ov.inviteCount,
      ov.invitesCount,
      ov.totalInvites,
      totals.inviteCount,
      totals.invitesCount,
      totals.totalInvites,
      safeOffer.inviteCount,
      safeOffer.invitesCount,
      safeOffer.totalInvites
    ),

    activeInviteCount: pickNumber(
      ov.activeInviteCount,
      ov.activeInvitesCount,
      ov.activeInvites,
      totals.activeInviteCount,
      totals.activeInvitesCount,
      totals.activeInvites,
      safeOffer.activeInviteCount,
      safeOffer.activeInvitesCount,
      safeOffer.activeInvites
    ),

    acceptedInviteCount: pickNumber(
      ov.acceptedInviteCount,
      ov.acceptedInvitesCount,
      ov.acceptedInvites,
      ov.acceptedCount,
      totals.acceptedInviteCount,
      totals.acceptedInvitesCount,
      totals.acceptedInvites,
      totals.acceptedCount,
      safeOffer.acceptedInviteCount,
      safeOffer.acceptedInvitesCount,
      safeOffer.acceptedInvites,
      safeOffer.acceptedCount
    ),

    onboardingSubmittedCount: pickNumber(
      ov.onboardingSubmittedCount,
      ov.submittedOnboardingCount,
      ov.submittedOnboardings,
      totals.onboardingSubmittedCount,
      totals.submittedOnboardingCount,
      totals.submittedOnboardings,
      safeOffer.onboardingSubmittedCount,
      safeOffer.submittedOnboardingCount,
      safeOffer.submittedOnboardings
    ),

    clicksCount: pickNumber(
      ov.clicksCount,
      ov.clicks,
      ov.totalClicks,
      totals.clicksCount,
      totals.clicks,
      totals.totalClicks,
      safeOffer.clicksCount,
      safeOffer.clicks,
      safeOffer.totalClicks
    ),

    redemptionCount: pickNumber(
      ov.redemptionCount,
      ov.redemptions,
      ov.redemptionsCount,
      ov.totalRedemptions,
      totals.redemptionCount,
      totals.redemptions,
      totals.redemptionsCount,
      totals.totalRedemptions,
      safeOffer.redemptionCount,
      safeOffer.redemptions,
      safeOffer.redemptionsCount,
      safeOffer.totalRedemptions
    ),

    revenueValue: pickNumber(
      ov.revenueValue,
      ov.revenue,
      ov.totalRevenue,
      totals.revenueValue,
      totals.revenue,
      totals.totalRevenue,
      safeOffer.revenueValue,
      safeOffer.revenue,
      safeOffer.totalRevenue
    ),

    unattributedCount: pickNumber(
      ov.unattributedCount,
      ov.unattributedRedemptions,
      ov.unattributed_redemptions,
      totals.unattributedCount,
      totals.unattributedRedemptions,
      totals.unattributed_redemptions,
      safeOffer.unattributedCount,
      safeOffer.unattributedRedemptions,
      safeOffer.unattributed_redemptions
    ),

    topInviteId:
      pickFirst(
        topInvite.inviteId,
        topInvite.id,
        topInvite.code,
        topInvite.handle
      ) || "",

    topInviteRedemptions: pickNumber(
      topInvite.redemptions,
      topInvite.redemptionsCount,
      topInvite.totalRedemptions
    ),

    currency: pickFirst(
      ov.currency,
      totals.currency,
      safeOffer.currency,
      "GBP"
    ),

    landingUrl:
      pickFirst(
        destinationNode.landingUrl,
        destinationNode.landingURL,
        destinationNode.url,
        ov.landingUrl,
        ov.landingURL,
        ov.destinationUrl,
        ov.url,
        safeOffer.landingUrl,
        safeOffer.landingURL,
        safeOffer.destinationUrl,
        safeOffer.url
      ) || "",

    publicPageUrl:
      pickFirst(
        destinationNode.publicPageUrl,
        destinationNode.public_page_url,
        ov.publicPageUrl,
        ov.public_page_url,
        safeOffer.publicPageUrl,
        safeOffer.public_page_url
      ) || "",

    pageSlug:
      pickFirst(
        destinationNode.pageSlug,
        destinationNode.page_slug,
        ov.pageSlug,
        ov.page_slug,
        safeOffer.pageSlug,
        safeOffer.page_slug
      ) || "",

    destinationMode:
      pickFirst(
        destinationNode.destinationMode,
        destinationNode.destination_mode,
        ov.destinationMode,
        ov.destination_mode,
        safeOffer.destinationMode,
        safeOffer.destination_mode
      ) || "",

    landingPageEnabled:
      pickBoolean(
        destinationNode.landingPageEnabled,
        destinationNode.landing_page_enabled,
        ov.landingPageEnabled,
        ov.landing_page_enabled,
        safeOffer.landingPageEnabled,
        safeOffer.landing_page_enabled
      ),

    customerActionType:
      pickFirst(
        destinationNode.customerActionType,
        destinationNode.customer_action_type,
        ov.customerActionType,
        ov.customer_action_type,
        safeOffer.customerActionType,
        safeOffer.customer_action_type
      ) || "",

    status:
      pickFirst(
        ov.status,
        offerNode.status,
        promoNode.status,
        safeOffer.status
      ) || "",

    startsAt:
      pickFirst(
        promoNode.startsAt,
        promoNode.starts_at,
        ov.startsAt,
        ov.starts_at,
        safeOffer.startsAt,
        safeOffer.starts_at
      ) || "",

    expiresAt:
      pickFirst(
        promoNode.expiresAt,
        promoNode.expires_at,
        ov.expiresAt,
        ov.expires_at,
        safeOffer.expiresAt,
        safeOffer.expires_at
      ) || "",

    isOngoing:
      pickBoolean(
        promoNode.isOngoing,
        promoNode.is_ongoing,
        ov.isOngoing,
        ov.is_ongoing,
        safeOffer.isOngoing,
        safeOffer.is_ongoing
      ),

    discountType:
      pickFirst(
        promoNode.discountType,
        promoNode.discount_type,
        ov.discountType,
        ov.discount_type,
        safeOffer.discountType,
        safeOffer.discount_type
      ) || "",

    discountValue: pickFirst(
      promoNode.discountValue,
      promoNode.discount_value,
      ov.discountValue,
      ov.discount_value,
      safeOffer.discountValue,
      safeOffer.discount_value
    ),

    discountText:
      pickFirst(
        promoNode.discountText,
        promoNode.discount_text,
        ov.discountText,
        ov.discount_text,
        safeOffer.discountText,
        safeOffer.discount_text
      ) || "",

    spendValue,
    cost: spendValue,
    spend: spendValue,

    roiValue,
    roiMultiple: roiValue,
    roi: roiValue,

    conversionRate,
  };
}